import React from 'react';
import { ReactNode, useReducer } from 'react';
import { CkOutput } from '../CkRichTextEditor/CkOutput';
import NavButton from './components/NavButton';
import QuizOption from './components/QuizOption';
import TeX from '@matejmazur/react-katex';

export interface QuizProps<T> {
  onAnswer?: (question: T) => (option: unknown) => void;
  data: T[];
  accessor: {
    optionsKey: keyof T;
    labelKey: keyof T;
    labelOptionKey: string;
  };
}
const initialStateReducer = {
  missedQuestions: [],
  index: 0,
  leftQuestionsClick: 0,
};
interface States {
  missedQuestions: any[];
  index: number;
  leftQuestionsClick: number;
  direction?: number;
}
interface ReducerAction {
  type:
    | 'add-missed-question'
    | 'clear-missed-question'
    | 'navigate'
    | 'go-to-missed'
    | 'reset-navigate';

  payload: States;
}

function reducer(state: States, action: ReducerAction) {
  switch (action.type) {
    case 'add-missed-question':
      return {
        ...state,
        missedQuestions: [...state.missedQuestions, action.payload.index],
      };
    case 'clear-missed-question':
      return {
        ...state,
        missedQuestions: [],
      };
    case 'navigate':
      return {
        ...state,
        index: state.index + (action.payload?.direction || 0),
      };
    case 'go-to-missed':
      return {
        ...state,
        index: state.missedQuestions[state.missedQuestions.length - 1],
      };
    case 'reset-navigate':
      return {
        ...state,
        index: 0,
      };
    default:
      return state;
  }
}
export function Quiz<T>(props: QuizProps<T>) {
  const {
    onAnswer,
    data,
    accessor: { labelKey, labelOptionKey, optionsKey },
  } = props;

  const { length } = data;
  const [state, dispatch] = useReducer(reducer, initialStateReducer);
  const { index } = state;
  const question = data[index];
  return (
    <div className="bg-white m-auto rounded mx-3">
      {' '}
      <div className="flex flex-col my-6 max-w-full">
        <div className="overflow-x-auto">
          <span className="text-xs text-gray-600 font-semibold">
            Question {index + 1}
          </span>
          {'hasMath' in (question as any) &&
          !!(question as { hasMath?: boolean })['hasMath'] ? (
            <TeX>{`${index + 1} - ${question[labelKey]}`}</TeX>
          ) : (
            <CkOutput data={question[labelKey] as ReactNode} />
          )}
        </div>
      </div>
      <hr />
      <div className="mt-6">
        {React.Children.toArray(
          (question[optionsKey] as Array<unknown>)
            .slice(0)
            .map((option, index) => {
              return (
                <QuizOption
                  option={option}
                  index={index}
                  labelOptionKey={labelOptionKey}
                  onAnswer={onAnswer?.(question)}
                  math={
                    'hasMath' in (option as object) &&
                    !!(option as { hasMath?: boolean })['hasMath']
                  }
                />
              );
            })
        )}
      </div>
      <div className="flex justify-between items-center">
        <div className="flex items-center justify-center"></div>
        <div className="flex flex-col items-center justify-center">
          <NavButton
            first={index + 1 === 1}
            last={index + 1 === length}
            setIndex={dispatch}
          />
        </div>
      </div>
    </div>
  );
}

export default Quiz;
